import React, { useState, useEffect, useRef } from 'react';
import FeatureCard from './FeatureCard';
import { FEATURES } from './featuresData';
import { CostReductionSVG, InstantSupportSVG, GrowthChartSVG } from './FeatureSVGs';
import { MessageCircle, Brain, TrendingUp } from 'lucide-react';

// Pipeline flow animation component
const PipelineFlow = ({ active }) => {
    return (
        <div className="absolute inset-0 overflow-hidden">
            {[...Array(15)].map((_, i) => (
                <div
                    key={i}
                    className={`absolute h-2 w-2 rounded-full bg-gradient-to-r from-blue-400 to-amber-400 
                              ${active ? 'animate-flow-down' : 'opacity-0'}`}
                    style={{
                        left: '50%',
                        top: `-10%`,
                        animationDelay: `${i * 0.5}s`,
                        animationDuration: '3s',
                    }}
                ></div>
            ))}
        </div>
    );
};

const Features = () => {
    const [activeFeature, setActiveFeature] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const timeoutRef = useRef(null);

    // Auto-advance the active feature every 7 seconds
    useEffect(() => {
        if (!isPaused) {
            timeoutRef.current = setTimeout(() => {
                setActiveFeature((prev) => (prev + 1) % FEATURES.length);
            }, 7000);
        }

        return () => clearTimeout(timeoutRef.current);
    }, [activeFeature, isPaused]);

    // Handle mouse interactions
    const handleMouseEnter = (index) => {
        setActiveFeature(index);
        setIsPaused(true);
    };

    const handleMouseLeave = () => {
        setIsPaused(false);
    };

    // SVG mapping function updated for new lifecycle stages
    const getFeatureSVG = (title) => {
        if (title.includes('Build')) return CostReductionSVG;
        if (title.includes('Learn')) return InstantSupportSVG;
        if (title.includes('Grow')) return GrowthChartSVG;
        return GrowthChartSVG; // Default fallback
    };

    // Define color schemes based on index
    const colorSchemes = [
        {
            color: "from-blue-600 to-blue-400",
            borderColor: "border-blue-500",
            shadowColor: "shadow-blue-500/20"
        },
        {
            color: "from-indigo-600 to-indigo-400",
            borderColor: "border-indigo-500",
            shadowColor: "shadow-indigo-500/20"
        },
        {
            color: "from-amber-600 to-amber-400",
            borderColor: "border-amber-500",
            shadowColor: "shadow-amber-500/20"
        }
    ];

    return (
        <section className="relative z-10 py-24 px-4">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <div className="inline-block px-4 py-1.5 bg-gradient-to-r from-blue-900/30 to-amber-900/30 rounded-full text-blue-400 text-sm font-medium mb-4 border border-blue-700/30">
                        <div className="flex items-center gap-2">
                            <MessageCircle className="w-4 h-4" />
                            <span>AI-Powered Conversation</span>
                            <span className="mx-1">�</span>
                            <span>Fueling Customer Relationships</span>
                            <Brain className="w-4 h-4 text-amber-400" />
                        </div>
                    </div>
                    <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight max-w-4xl mx-auto">
                        <span className="text-white">Transform Conversations into </span>
                        <span className="bg-gradient-to-r from-blue-400 to-amber-400 bg-clip-text text-transparent">Business Value</span>
                    </h2>
                    <p className="text-xl text-slate-300 leading-relaxed mb-10 max-w-3xl mx-auto">
                        Just as oil refining creates valuable products, our chatbot platform turns everyday conversations
                        into actionable insights and measurable business results.
                    </p>

                    {/* Feature indicators */}
                    <div className="inline-flex items-center gap-3 px-4 py-2 bg-slate-800/80 rounded-full border border-slate-700/50 mb-8">
                        {FEATURES.map((feature, idx) => (
                            <button
                                key={idx}
                                className={`w-3 h-3 rounded-full transition-all duration-300 ${activeFeature === idx ? 'bg-gradient-to-r ' + colorSchemes[idx].color + ' shadow-md ' + colorSchemes[idx].shadowColor : 'bg-slate-600'}`}
                                onClick={() => setActiveFeature(idx)}
                                aria-label={`View ${feature.title}`}
                            ></button>
                        ))}
                    </div>
                </div>

                {/* Features Content */}
                <div className="relative max-w-6xl mx-auto" onMouseLeave={handleMouseLeave}>
                    {/* Oil pipeline connector - vertical pipeline */}
                    <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-10 bg-gradient-to-b from-slate-800 via-slate-800 to-slate-800 transform -translate-x-1/2 rounded-full border-l border-r border-blue-500/30">
                        {/* Pipeline segments */}
                        {[...Array(12)].map((_, i) => (
                            <div
                                key={i}
                                className="absolute w-full h-6 border-t border-b border-blue-500/30"
                                style={{ top: `${i * 8}%` }}
                            ></div>
                        ))}

                        {/* Data flow animation */}
                        <PipelineFlow active={true} />
                    </div>

                    {/* Feature Cards */}
                    <div className="space-y-24 relative z-10">
                        {FEATURES.map((feature, index) => {
                            // Get the appropriate SVG component
                            const SvgComponent = getFeatureSVG(feature.title);
                            const colorScheme = colorSchemes[index % colorSchemes.length];

                            return (
                                <div key={index} className="relative" onClick={() => setActiveFeature(index)}>
                                    {/* Connector to pipeline - horizontal pipeline */}
                                    <div className="hidden md:block absolute top-1/2 -left-14 h-8 w-20 bg-gradient-to-r from-slate-800 to-slate-800 transform -translate-y-1/2 rounded-full border border-blue-500/30">
                                        {/* Connector segments */}
                                        {[...Array(3)].map((_, i) => (
                                            <div
                                                key={i}
                                                className="absolute h-full w-4 border-l border-r border-blue-500/30"
                                                style={{ left: `${i * 25}%` }}
                                            ></div>
                                        ))}

                                        {/* Data flow in connector */}
                                        {activeFeature === index && (
                                            <div className="absolute inset-0 overflow-hidden">
                                                {[...Array(5)].map((_, i) => (
                                                    <div
                                                        key={i}
                                                        className="absolute h-2 w-2 rounded-full bg-gradient-to-r from-blue-400 to-amber-400 animate-flow-right"
                                                        style={{
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            left: '-10%',
                                                            animationDelay: `${i * 0.5}s`,
                                                            animationDuration: '2s'
                                                        }}
                                                    ></div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Valve indicator for active feature */}
                                    {activeFeature === index && (
                                        <div className="hidden md:flex absolute -left-24 top-1/2 transform -translate-y-1/2 items-center justify-center">
                                            <div className={`relative w-16 h-16 rounded-full border-4 border-slate-800 ${colorScheme.shadowColor.replace('/20', '/50')} flex items-center justify-center overflow-hidden`}>
                                                <div className={`absolute inset-0 bg-gradient-to-r ${colorScheme.color} opacity-20`}></div>
                                                <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${colorScheme.color} flex items-center justify-center animate-spin-slow`}>
                                                    <feature.icon className="w-8 h-8 text-white" />
                                                </div>
                                            </div>
                                            <div className="absolute w-4 h-10 bg-slate-800 rounded-full border border-blue-500/30 flex items-center justify-center">
                                                <div className="w-1 h-6 bg-blue-500/30 rounded-full"></div>
                                            </div>
                                        </div>
                                    )}

                                    <div className={`transform transition-all duration-500 ${activeFeature === index ? 'scale-100 opacity-100' : 'scale-95 opacity-70'}`}>
                                        <FeatureCard
                                            {...feature}
                                            ProcessSVG={SvgComponent}
                                            color={colorScheme.color}
                                            borderColor={colorScheme.borderColor}
                                            shadowColor={colorScheme.shadowColor}
                                            active={activeFeature === index}
                                            onMouseEnter={() => handleMouseEnter(index)}
                                        />
                                    </div>

                                    {/* Progress indicator */}
                                    {activeFeature === index && (
                                        <div className="absolute -right-4 bottom-10 hidden md:block">
                                            <div className="relative h-24 w-2 bg-slate-700/50 rounded-full overflow-hidden">
                                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-500 to-amber-500 animate-progress rounded-full" style={{ animationDuration: "7s" }}></div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Action button */}
                    <div className="flex justify-center mt-16">
                        <button
                            className="bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white px-8 py-4 rounded-full font-medium flex items-center gap-2 transform transition-transform hover:scale-105 shadow-lg">
                            <span>Find Out How</span>
                            <TrendingUp className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Features;